/** @jsxImportSource @emotion/react */

import { yupResolver } from "@hookform/resolvers/yup";
import { Button, Modal, notification } from "antd";
import { AuthApi } from "api/auth";
import { ChangePasswordFormType } from "api/auth/type";
import InputCustom from "components/base/InputCustom";
import { REGEX_PASSWORD } from "constants/regex";
import { ResponseCode } from "constants/response";
import { useAppDispatch, useAppSelector } from "hooks/app-hook";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { selectUsername } from "store/auth";
import { setOpenModal } from "store/modal";
import { ModalTypeValue } from "types/modal";
import * as yup from "yup";

import { ChangePasswordModalStyle } from "./style.index";

const ChangePasswordModal = () => {
  const dispatch = useAppDispatch();
  const username = useAppSelector(selectUsername);
  const { t } = useTranslation(["common", "modal"]);

  const validateForm = yup
    .object({
      username: yup.string().required(),
      oldpass: yup
        .string()
        .required(t("modal:field_required"))
        .matches(REGEX_PASSWORD, t("modal:password_requirements")),
      password: yup
        .string()
        .required(t("modal:field_required"))
        .matches(REGEX_PASSWORD, t("modal:password_requirements")),
      passconf: yup
        .string()
        .required(t("modal:field_required"))
        .oneOf([yup.ref("password")], t("modal:password_mismatch"))
        .matches(REGEX_PASSWORD, t("modal:password_requirements"))
    })
    .required();

  const [api, contextHolder] = notification.useNotification();

  const [loading, setLoading] = useState<boolean>(false);

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isDirty }
  } = useForm<ChangePasswordFormType>({
    defaultValues: {
      username,
      oldpass: "",
      password: "",
      passconf: ""
    },
    mode: "all",
    resolver: yupResolver(validateForm)
  });

  const openNotification = (message: string, status: "success" | "error") => {
    api[status]({
      message,
      description: ""
    });
  };

  const onSubmit = async (data: ChangePasswordFormType) => {
    setLoading(true);

    const { msg, status } = await AuthApi.changePassword(username, {
      ...data,
      username
    });

    setLoading(false);

    if (status === ResponseCode.SUCCESS) {
      openNotification(t("modal:change_success"), "success");
      reset();
      return;
    } else {
      openNotification(msg, "error");
      return;
    }
  };
  return (
    <Modal
      open={true}
      forceRender
      title={t("modal:edit_password_title")}
      onCancel={() => dispatch(setOpenModal(ModalTypeValue.Close))}
      footer={false}
      centered
      focusTriggerAfterClose={false}
      css={ChangePasswordModalStyle.wrapper}
    >
      <div css={ChangePasswordModalStyle.formWrapper}>
        <div css={ChangePasswordModalStyle.describeFrom}>
          {t("modal:password_hint")}
        </div>
        <form onSubmit={handleSubmit(onSubmit)}>
          <label>{t("modal:current_password")}</label>
          <InputCustom
            control={control}
            type="password"
            value={watch("oldpass")}
            name="oldpass"
            placeholder={t("modal:current_password_placeholder")}
            errors={errors.oldpass?.message}
            validate_status={errors.oldpass ? "error" : "success"}
          />
          <label>{t("modal:new_password")}</label>
          <InputCustom
            control={control}
            type="password"
            name="password"
            value={watch("password")}
            placeholder={t("modal:new_password_placeholder")}
            validate_status={errors.password ? "error" : "success"}
            errors={errors.password?.message}
          />
          <label>{t("modal:confirm_new_password")}</label>
          <InputCustom
            control={control}
            name="passconf"
            type="password"
            value={watch("passconf")}
            placeholder={t("modal:confirm_new_password_placeholder")}
            validate_status={errors.passconf ? "error" : "success"}
            errors={errors.passconf?.message}
          />
          <Button
            loading={loading}
            disabled={!isDirty}
            htmlType="submit"
            type="primary"
          >
            {t("modal:confirm_button")}
          </Button>
          {contextHolder}
        </form>
      </div>
    </Modal>
  );
};

export default ChangePasswordModal;
